/**
 * Wd local shared functions service (wd-local-shared-functions.service.ts).
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2021-2024 Build Centrix
 */
import { Inject } from '@angular/core';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WdLocalEnvironmentService } from '../environment/wd-local-environment.service';
import { HttpParams } from '@angular/common/http';
import {
  AllowedLookupTypes,
  OrderTypes,
  OrderUiSections,
} from '../../interfaces/orders/order-interfaces';
import {
  IApiUrls,
  ILimits,
  ITranslationKeys,
  IUnits,
  IBcxErrorService,
  IBcxSharedFunctionsService,
} from '../../interfaces/generic/generic-interfaces';
import { IVehicleAssignmentModel } from '../../interfaces/vehicles/vehicle-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdLocalSharedFunctionsService {
  /**
   * Constructor Initialization
   * @since 4.26.0
   */
  constructor(
    public router: Router,
    private wdEnvironmentService: WdLocalEnvironmentService,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdSharedFunctionsService')
    public wdSharedFunctionsService: IBcxSharedFunctionsService,
  ) {
    this.setCommonApiUrls();
    this.setCommonTranslationKeys();
    this.setCommonConfigurations();
    this.setCommonUnits();
  }

  // Pagination Variables
  pageSizeOptions: Array<any> = [15, 25, 50, 75, 100];
  pageSortDirectionOptions: Array<any> = ['asc', 'desc'];

  // API Urls
  apiUrls: IApiUrls = {};

  // Translation Keys
  translationKeys: ITranslationKeys = {};

  // Units of Measurement
  units: IUnits = {};

  // Limits
  limits: ILimits = {};

  /********************************************* Static Functions ***************************************************/

  /**
   * Convert float time in hours to HH:MM format
   *
   * @since 4.31.0
   * @memberof WdLocalSharedFunctionsService
   */
  static convertFloatHoursToHoursAndMinutes(time: number, format?: string) {
    let newTime: string = format ? '' : '0:00';

    const isNegative = time < 0;
    time = Math.abs(time); // Work with the absolute value for calculations

    const isLongTime = format === 'long';
    const isShortTime = format === 'short';
    const hoursFloored = Math.floor(time);
    const minutesRounded = Math.round((time - hoursFloored) * 60);
    const hours = hoursFloored.toString();
    let minutes = minutesRounded.toString();

    // If both hours and minutes are zero return
    if (hoursFloored === 0 && minutesRounded === 0) {
      return isLongTime ? '0 minutes' : isShortTime ? '0 mins' : newTime;
    }

    if (isLongTime) {
      if (hoursFloored < 1) {
        newTime =
          (isNegative ? '-' : '') +
          minutes +
          (minutesRounded === 1 ? ' minute' : ' minutes');
      } else {
        newTime =
          (isNegative ? '-' : '') +
          hours +
          (hoursFloored === 1 ? ' hour ' : ' hours ') +
          minutes +
          (minutesRounded === 1 ? ' minute' : ' minutes');
      }
    } else if (isShortTime) {
      if (hoursFloored < 1) {
        newTime =
          (isNegative ? '-' : '') +
          minutes +
          (minutesRounded === 1 ? ' min' : ' mins');
      } else {
        newTime =
          (isNegative ? '-' : '') +
          hours +
          (hoursFloored === 1 ? ' hr ' : ' hrs ') +
          minutes +
          (minutesRounded === 1 ? ' min' : ' mins');
      }
    } else {
      if (minutesRounded < 10) {
        // read "2:06" instead of "2:6"
        minutes = '0' + minutes;
      }
      newTime = (isNegative ? '-' : '') + hours + ':' + minutes;
    }

    return newTime;
  }

  /**
   * For use with JSON.parse to restore dates as datetime rather than string
   * @since 4.36.0
   * @memberof WdLocalSharedFunctionsService
   */
  static revertSectionDateReviver(_key: string, value: string) {
    let a: (string | number)[];
    if (typeof value === 'string') {
      a = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)(Z|([+-])(\d{2}):(\d{2}))$/.exec(
        value,
      );
      if (a) {
        return new Date(Date.UTC(+a[1], +a[2] - 1, +a[3], +a[4], +a[5], +a[6]));
      }
    }
    return value;
  }

  /********************************************************************************************************************/

  /**
   * Builds query params with the current filters
   *
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  buildFilters(serializedFilters: any): string {
    let params = new HttpParams();
    Object.keys(serializedFilters).map((key) => {
      if (serializedFilters[key] !== null) {
        params = params.set(key, serializedFilters[key]);
      }
    });
    return params.toString();
  }

  /**
   * Build Job Label based on Job Name and Number
   *
   * @since 4.70.0
   * @memberof WdLocalSharedFunctionsService
   */
  buildJobLabel(job: any) {
    const number = this.wdSharedFunctionsService.getDescendantProperty(
      job,
      'number',
    );
    const name = this.wdSharedFunctionsService.getDescendantProperty(
      job,
      'name',
    );

    return [number, name].join(' ');
  }

  /**
   * Build User Full Name
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  buildUserFullName(user: any) {
    const firstName = this.wdSharedFunctionsService.getDescendantProperty(
      user,
      'name.first',
    );
    const lastName = this.wdSharedFunctionsService.getDescendantProperty(
      user,
      'name.last',
    );

    return [firstName, lastName].join(' ');
  }

  /**
   * Filter the vehicle assignments based on an array of assignment ids.
   */
  getAssignmentsByIds(
    allAssignments: Array<IVehicleAssignmentModel> = [],
    assignmentIds: Array<string>,
  ): Array<IVehicleAssignmentModel> {
    return allAssignments.filter((assignment: IVehicleAssignmentModel) => {
      return assignmentIds.includes(assignment.id);
    });
  }

  /**
   * Filter the vehicle assignments based on an order Id.
   */
  getAssignmentsByOrderId(
    allAssignments: Array<IVehicleAssignmentModel> = [],
    orderId: string,
  ): Array<IVehicleAssignmentModel> {
    return allAssignments.filter((assignment: IVehicleAssignmentModel) => {
      return assignment.orders?.includes(orderId);
    });
  }

  /**
   * Filter the vehicle assignments based on the shipping date.
   */
  getAssignmentsByShippingDate(
    allAssignments: Array<IVehicleAssignmentModel> = [],
    shippingDate: Date,
  ): Array<IVehicleAssignmentModel> {
    return allAssignments.filter((assignment: IVehicleAssignmentModel) => {
      const start = new Date(assignment.date.start);
      const end = new Date(assignment.date.end);
      return shippingDate >= start && shippingDate <= end;
    });
  }

  /**
   * Get Selected Filter Values by Key
   *
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public getSelectedFiltersValuesByKey(filterArr: any, key: string) {
    return filterArr
      ?.filter((obj: any) => obj.selected)
      ?.map((obj: any) => obj[key]);
  }

  /**
   * Get Data Export API url
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public get dataExportApiUrl() {
    return this.apiUrls.dataExportApi;
  }

  /**
   * Get Model View Url
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public getModelViewerUrl(index: string): string {
    return this.wdEnvironmentService.resources.getOrderModelViewerUrl(index);
  }

  /**
   * Get Order API Path for Service
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public get orderApiUrl() {
    return this.apiUrls.orderApi;
  }

  /**
   * Get Manufacturing Admin API Path for Service
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public get manufacturingAdminApiUrl() {
    return this.apiUrls.manufacturingAdminApi;
  }

  /**
   * Get Ordering API Path for Service
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public get orderingApiUrl() {
    return this.apiUrls.orderingApi;
  }

  /**
   * Get Spooling API Path for Service
   */
  public get spoolingApiUrl() {
    return this.apiUrls.spoolingApi;
  }

  /**
   * Get Public API Path for Service
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public get publicApiUrl() {
    return this.apiUrls.publicApi;
  }

  /**
   * GET UI Section Type
   * Returns enum OrderUiSections
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  public get orderSectionType(): typeof OrderUiSections {
    return OrderUiSections;
  }

  /**
   * GET Allowed Lookups
   * Returns enum of allowed lookup type keys
   */
  public get allowedLookupTypeKey(): typeof AllowedLookupTypes {
    return AllowedLookupTypes;
  }

  /**
   * GET Order Type Key
   * Returns enum order types
   * @since 4.75.0
   */
  public get orderTypeKey(): typeof OrderTypes {
    return OrderTypes;
  }

  /**
   * Get User API Path for Service
   * @since 4.77.0
   * @memberof WdLocalSharedFunctionsService
   */
  public get userApiUrl() {
    return this.apiUrls.userApi;
  }

  /**
   * Compare two arrays
   */
  isSameArray(arr1: Array<any>, arr2: Array<any>) {
    return JSON.stringify(arr1) === JSON.stringify(arr2);
  }

  /**
   * Set common API urls
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  setCommonApiUrls() {
    // Set tha data export API url
    this.apiUrls.dataExportApi = this.wdEnvironmentService.configuration.getDataExportApiPath;
    this.apiUrls.orderApi = this.wdEnvironmentService.configuration.getOrderApiPath;
    this.apiUrls.orderingApi = this.wdEnvironmentService.configuration.getBaseApiPath;
    this.apiUrls.manufacturingAdminApi = this.wdEnvironmentService.configuration.getManufacturingAdminApiPath;
    this.apiUrls.publicApi = this.wdEnvironmentService.configuration.getPublicApiPath;
    this.apiUrls.userApi = this.wdEnvironmentService.configuration.getUserApiPath;
    this.apiUrls.spoolingApi = this.wdEnvironmentService.configuration.getSpoolingApiPath;
  }

  /**
   * Set common limits
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  setCommonConfigurations() {
    this.limits.maxFileSize = this.wdEnvironmentService.configuration
      .getMaximumFileSize
      ? parseInt(this.wdEnvironmentService.configuration.getMaximumFileSize)
      : null;
    this.limits.maxFileUploads = this.wdEnvironmentService.configuration
      .getMaximumFileUploads
      ? parseInt(this.wdEnvironmentService.configuration.getMaximumFileUploads)
      : null;
    this.limits.maxFilePostSize = this.wdEnvironmentService.configuration
      .getMaximumFilePostSize
      ? parseInt(this.wdEnvironmentService.configuration.getMaximumFilePostSize)
      : null;
    this.limits.minimumProductionDaysRequired = this.wdEnvironmentService
      .constants.getCompanyMinimumDaysRequired
      ? parseInt(
          this.wdEnvironmentService.constants.getCompanyMinimumDaysRequired,
        )
      : 0;
  }

  /**
   * Set common keys used for translations
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  setCommonTranslationKeys() {
    // Currency Symbol for Translations
    this.translationKeys.currencySymbol =
      'locales.currency_symbol_' +
      this.wdEnvironmentService.constants.getLocaleCurrencyUnit;
    // Weight Unit for Translations
    this.translationKeys.weightUnit =
      'locales.weight_' +
      this.wdEnvironmentService.constants.getLocaleWeightUnit;
  }

  /**
   * Set common units
   * @since 4.71.0
   * @memberof WdLocalSharedFunctionsService
   */
  setCommonUnits() {
    // Currency Unit
    this.units.currency = this.wdEnvironmentService.constants.getLocaleCurrencyUnit;
  }

  /**
   * Parse Errors
   */
  parseErrors(error: any, operation: string) {
    const data = error.data;
    const status = error.status;
    const statusText = error.statusText;
    const statusType = error.statusType;

    // Validation Errors
    if (status === 4000 && statusType === 'validation_error') {
      for (const key in data) {
        // eslint-disable-next-line no-prototype-builtins
        if (data.hasOwnProperty(key)) {
          this.wdErrorService.userError(
            'Validation Error',
            '"' + data[key].value + '" is not a valid ' + key,
          );
        }
      }
    } else if (status === 4000 && statusType === 'client_error') {
      const reason = 'Error ' + operation;
      this.wdErrorService.userError('Client Error', statusText || reason);
    } else if (status === 5000 && statusType === 'server_error') {
      const reason = 'Error ' + operation;
      this.wdErrorService.userError('Server Error', statusText || reason);
    } else if (status === 4010 && statusType === 'authentication_error') {
      const error = `${operation}. ${statusText}, Please contact <a href="mailto:support@buildcentrix.com">support@buildcentrix.com.</a>`;
      this.wdErrorService.userError('Authentication Error', error);
    } else if (status === 2400 && statusType === 'success_no_content') {
      this.wdErrorService.userError(operation, statusText);
    } else {
      this.wdErrorService.userError(
        'Error',
        'Error ' + operation + '. ' + statusText,
      );
    }
  }
}
